import { BookingConstraintDto } from '../../dtos/booking-constraint.dto';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { WeekDayPipe } from '../../pipes/week-day.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { FormsModule } from '@angular/forms';
import { BookingConstraintStringDto } from '../../dtos/booking-constraint-string.dto';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-restrict-booking-times',
  imports: [MatDialogModule, WeekDayPipe, MatButtonModule, MatIconModule, MatCardModule, MatFormFieldModule, MatInputModule, MatTimepickerModule, FormsModule, TranslocoPipe],
  templateUrl: './restrict-booking-times.dialog.html',
  styleUrl: './restrict-booking-times.dialog.scss'
})
export class RestrictBookingTimesDialog implements OnInit {
  bookingContraintStringDtos!: BookingConstraintStringDto[];
  copiedWeekday?: number;
  error?: string;

  constructor(private dialogRef: MatDialogRef<RestrictBookingTimesDialog>, private translocoService: TranslocoService, @Inject(MAT_DIALOG_DATA) public bookingConstraints: BookingConstraintDto[]) {
  }

  ngOnInit(): void {
    if (!this.bookingConstraints) {
      this.bookingConstraints = [];

      for (let weekDay = 0; weekDay <= 6; weekDay++) {
        this.bookingConstraints.push({ weekDay, startTimeMinutesAfterMidnight: 0, endTimeMinutesAfterMidnight: 1440 });
      }
    }

    this.bookingContraintStringDtos = this.bookingConstraints.map((constraint) => {
      return {
        weekDay: constraint.weekDay,
        startTimeMinutesAfterMidnight: this.showTime(constraint.startTimeMinutesAfterMidnight),
        endTimeMinutesAfterMidnight: this.showTime(constraint.endTimeMinutesAfterMidnight)
      };
    });
  }

  bookingConstraintsWeekday(weekday: number): BookingConstraintStringDto[] {
    if (!this.bookingContraintStringDtos) {
      return [];
    }

    return this.bookingContraintStringDtos.filter((constraint) => constraint.weekDay === weekday);
  }

  deleteBookingConstraint(bookingConstraintStringDto: BookingConstraintStringDto): void {
    this.bookingContraintStringDtos = this.bookingContraintStringDtos.filter((constraint) => constraint !== bookingConstraintStringDto);
  }

  addBookingConstraint(weekday: number): void {
    this.bookingContraintStringDtos.push({
      weekDay: weekday,
      startTimeMinutesAfterMidnight: '00:00',
      endTimeMinutesAfterMidnight: '00:00'
    });
  }

  copyBookingConstraints(weekday: number): void {
    this.copiedWeekday = weekday;
  }

  pasteBookingConstraints(weekday: number): void {
    this.bookingContraintStringDtos = this.bookingContraintStringDtos.filter((constraint) => constraint.weekDay !== weekday);

    let copiedConstraints = this.bookingContraintStringDtos.filter((constraint) => constraint.weekDay === this.copiedWeekday);

    let pastedConstraints = JSON.parse(JSON.stringify(copiedConstraints));
    pastedConstraints = pastedConstraints.map((constraint: BookingConstraintStringDto) => {
      constraint.weekDay = weekday;
      return constraint;
    });

    this.bookingContraintStringDtos = this.bookingContraintStringDtos.concat(pastedConstraints);
  }

  deleteBookingContraints(weekday: number): void {
    this.bookingContraintStringDtos = this.bookingContraintStringDtos.filter((constraint) => constraint.weekDay !== weekday);
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    let newBookingConstraints: BookingConstraintDto[] = [];
    for (let constraint of this.bookingContraintStringDtos) {
      let newStart = this.calculateMinutesFromMidnight(constraint.startTimeMinutesAfterMidnight);
      let newEnd = this.calculateMinutesFromMidnight(constraint.endTimeMinutesAfterMidnight);

      if (newStart == undefined || newEnd == undefined) {
        this.error = this.translocoService.translate("error.restrict_booking_times.time_format");
        return;
      }

      if (newStart < 0 || newStart > 1440 || newEnd < 0 || newEnd > 1440) {
        this.error = this.translocoService.translate("error.restrict_booking_times.time_invalid");
        return;
      }

      newBookingConstraints.push({
        weekDay: constraint.weekDay,
        startTimeMinutesAfterMidnight: newStart,
        endTimeMinutesAfterMidnight: newEnd
      });
    }

    for (let weekDay = 0; weekDay <= 6; weekDay++) {
      let overlaps = this.overlap(newBookingConstraints.filter((constraint) => constraint.weekDay === weekDay));
      if (overlaps) {
        this.error = this.translocoService.translate("error.restrict_booking_times.overlap");
        return;
      }
    }

    this.dialogRef.close(newBookingConstraints);
  }

  showTime(minutesFromMidnight: number): string {
    if (Number.isNaN(minutesFromMidnight)) {
      return '00:00';
    }

    const hours = Math.floor(minutesFromMidnight / 60);
    const minutes = minutesFromMidnight % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  calculateMinutesFromMidnight(time: string): number | undefined {
    let parts = time.split(':');
    let hours = parseInt(parts[0]);
    let minutes = parseInt(parts[1]);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return undefined;
    }

    return hours * 60 + minutes;
  }

  overlap(constraints: BookingConstraintDto[]): boolean {
    if (constraints.length < 2) {
      return false;
    }

    let sortedConstraints = constraints.sort((a, b) => a.startTimeMinutesAfterMidnight - b.startTimeMinutesAfterMidnight);

    for (let i = 1; i < sortedConstraints.length; i++) {
      if (sortedConstraints[i].startTimeMinutesAfterMidnight < sortedConstraints[i - 1].endTimeMinutesAfterMidnight) {
        return true;
      }
    }

    return false;
  }
}
